/**
 * ChatImageViewer — fullscreen image lightbox for DM chat.
 * Backdrop blur overlay, swipe between images, close on tap/X.
 * Premium minimalist design matching Turnifly dark theme.
 */

import React, { useState, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

// ── Types ──────────────────────────────────────────────────

interface ChatImageViewerProps {
  images: string[]
  initialIndex?: number
  isOpen: boolean
  onClose: () => void
}

// ── Component ──────────────────────────────────────────────

export const ChatImageViewer: React.FC<ChatImageViewerProps> = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const hasMultiple = images.length > 1

  const goNext = useCallback((): void => {
    setCurrentIndex((i) => (i + 1) % images.length)
  }, [images.length])

  const goPrev = useCallback((): void => {
    setCurrentIndex((i) => (i - 1 + images.length) % images.length)
  }, [images.length])

  // Handle swipe gestures
  const handleDragEnd = useCallback(
    (_e: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number } }): void => {
      if (Math.abs(info.offset.x) > 50) {
        if (info.offset.x > 0) goPrev()
        else goNext()
      }
    },
    [goNext, goPrev],
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full
              bg-white/[0.1] backdrop-blur-sm flex items-center justify-center
              hover:bg-white/[0.15] transition-colors tap-highlight-transparent"
            style={{ top: 'max(16px, calc(var(--tg-safe-area-inset-top) + 8px))' }}
          >
            <X size={18} className="text-white" />
          </button>

          {/* Counter */}
          {hasMultiple && (
            <div
              className="absolute top-4 left-1/2 -translate-x-1/2 z-10
                px-3 py-1 rounded-full bg-white/[0.1] backdrop-blur-sm"
              style={{ top: 'max(16px, calc(var(--tg-safe-area-inset-top) + 8px))' }}
            >
              <span className="text-xs font-medium text-white/80">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          )}

          {/* Image */}
          <motion.div
            key={currentIndex}
            drag={hasMultiple ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleDragEnd}
            className="relative z-[1] max-w-[92vw] max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              key={images[currentIndex]}
              src={images[currentIndex]}
              alt=""
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="max-w-full max-h-[80vh] rounded-lg object-contain select-none"
              draggable={false}
            />
          </motion.div>

          {/* Navigation arrows (desktop) */}
          {hasMultiple && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev() }}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10
                  w-10 h-10 rounded-full bg-white/[0.08] backdrop-blur-sm
                  flex items-center justify-center
                  hover:bg-white/[0.15] transition-colors
                  tap-highlight-transparent hidden sm:flex"
              >
                <ChevronLeft size={18} className="text-white/70" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext() }}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10
                  w-10 h-10 rounded-full bg-white/[0.08] backdrop-blur-sm
                  flex items-center justify-center
                  hover:bg-white/[0.15] transition-colors
                  tap-highlight-transparent hidden sm:flex"
              >
                <ChevronRight size={18} className="text-white/70" />
              </button>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
